import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AccountsWithCustomerServiceRepResponse,
  listAccountsWithCustomerServiceRep,
} from '~/services/LuxLoyaltyCustomerServiceRepService';
import { resendInfiniteOnboardingEmail, setAccountCustomerServiceRep } from '~/services/LuxLoyaltyService';
import type { InfiniteOnboardingEmailResponse } from '~/services/LuxLoyaltyService';

import { customerServiceRepAssignmentsQueryKeys, customerServiceRepQueryKeys } from './customerServiceRepQueryKeys';
import { loyaltyAccountQueryKeys } from './useGetLoyaltyAccount';

interface UseGetAssignmentsParams {
  customerServiceRepId?: string;
  customerId?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

/** Paginated list of accounts with a CS rep mapped. Optionally filtered by rep and/or customer. */
export function useGetCustomerServiceRepAssignments({
  customerServiceRepId,
  customerId,
  page,
  pageSize,
  enabled = true,
}: UseGetAssignmentsParams = {}) {
  return useQuery({
    queryKey: customerServiceRepAssignmentsQueryKeys.list({
      customerServiceRepId,
      customerId,
      page,
      pageSize,
    }),
    queryFn: () => listAccountsWithCustomerServiceRep({ customerServiceRepId, customerId, page, pageSize }),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Returns a map of repId -> mapped-customer count by calling the assignments
 * endpoint once per rep with `pageSize: 1` and reading `pageInfo.totalRecords`. Cheap
 * because we never fetch row payloads, only counts.
 */
export function useCustomerServiceRepMemberCounts(repIds: ReadonlyArray<string>): {
  countsById: Record<string, number>;
  isLoading: boolean;
  isError: boolean;
} {
  const queries = useQueries({
    queries: repIds.map((repId) => ({
      queryKey: customerServiceRepAssignmentsQueryKeys.list({
        customerServiceRepId: repId,
        page: 1,
        pageSize: 1,
      }),
      queryFn: () => listAccountsWithCustomerServiceRep({ customerServiceRepId: repId, page: 1, pageSize: 1 }),
      staleTime: 30_000,
    })),
  });

  const countsById = repIds.reduce<Record<string, number>>((acc, repId, idx) => {
    const data = queries[idx]?.data as AccountsWithCustomerServiceRepResponse | undefined;
    acc[repId] = data?.result.pageInfo.totalRecords ?? 0;
    return acc;
  }, {});

  return {
    countsById,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
}

interface MutationOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface ResendInfiniteOnboardingEmailMutationOptions {
  onSuccess?: (data: InfiniteOnboardingEmailResponse) => void;
  onError?: (error: Error) => void;
}

/** Set or overwrite the rep linked to an existing Société account. */
export function useSetAccountCustomerServiceRep(options: MutationOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, customerServiceRepId }: { customerId: string; customerServiceRepId: string }) =>
      setAccountCustomerServiceRep({ customerId, customerServiceRepId }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: loyaltyAccountQueryKeys.byCustomer(variables.customerId) });
      void queryClient.invalidateQueries({ queryKey: customerServiceRepAssignmentsQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: customerServiceRepQueryKeys.lists() });
      options.onSuccess?.();
    },
    onError: options.onError,
  });
}

/** Re-send the Infinite onboarding email for an existing Infinite account. */
export function useResendInfiniteOnboardingEmail(options: ResendInfiniteOnboardingEmailMutationOptions = {}) {
  return useMutation({
    mutationFn: ({ customerId }: { customerId: string }) => resendInfiniteOnboardingEmail(customerId),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}
